'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { authBunja } from '@flipster/features/src/shared/auth/state';
import { earnSummaryBunja, getEarnSummaryOfCurrency } from '@flipster/features/src/shared/earn/state';
import { useRouter } from '@flipster/i18n/routing';
import { useOverlay } from '@flipster/overlay';
import { Button } from '@flipster/react/design-system';
import { css } from '@flipster/styled-system/css';
import { stack, vstack } from '@flipster/styled-system/patterns';
import { PusdtCurrency } from '@flipster/utils/constants/currency';
import segment from '@flipster/utils/segment';
import { EarnEvent } from '@flipster/utils/segment/events/earn';

import { useWalletModal } from '@web/app/[locale]/(web)/_modal/WalletModal/hooks/useWalletModal';
import PusdtNudgeModal from '@web/app/[locale]/(web)/_modal/premium-earn/pusdt-nudge-modal';

import { CurrencyHeader } from './currency-header';
import EarnActionModal from './earn-action-modal';

const InactiveAprCard = ({ currency }: { currency: string }) => {
  const router = useRouter();
  const overlay = useOverlay();
  const { openWalletModal } = useWalletModal();

  const { isLoggedInAtom } = useBunja(authBunja);
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);

  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const earnSummary = useAtomValue(earnSummaryAtom);

  const { baseAprPct, maxAprPct, isMintAvailable, isTradeAvailable } = getEarnSummaryOfCurrency(currency, earnSummary);

  // Open action modal when mint and trade is available
  const needToOpenActions = isMintAvailable || isTradeAvailable;
  const isPusdt = currency === PusdtCurrency;

  const onClickGetHigherApr = () => {
    segment.track(EarnEvent.EarnManageClick, { button_text: 'Get premium', currency });
    if (isLoggedIn) {
      overlay.open(<PusdtNudgeModal />);
    } else {
      router.push('/login');
    }
  };

  const onClickDeposit = () => {
    segment.track(EarnEvent.EarnManageClick, { button_text: 'Deposit', currency });
    if (isLoggedIn) {
      openWalletModal('deposit', { defaultCurrency: currency });
    } else {
      router.push('/login');
    }
  };

  const onClickAdd = () => {
    segment.track(EarnEvent.EarnManageClick, { button_text: 'Add', currency });
    overlay.open(<EarnActionModal currency={currency} />);
  };

  return (
    <div className={stack({ gap: 16, bg: 'background.default.level-1', p: 16, rounded: 8 })}>
      <CurrencyHeader currency={currency} />
      <div className={vstack({ gap: 2, alignSelf: 'center' })}>
        <p className={css({ textStyle: 'Title/M', color: 'content.accent.info' })}>{maxAprPct.toString()}% APR</p>
        <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
          {baseAprPct.toString()}% base APR, up to {maxAprPct.toString()}% with trading
        </p>
      </div>
      {needToOpenActions && (
        <Button label='Add' onClick={onClickAdd} css={{ w: { base: '100%', tablet: 296 }, alignSelf: 'center' }} />
      )}
      {!needToOpenActions && (
        <Button
          label={isPusdt ? 'Get premium' : 'Deposit'}
          onClick={isPusdt ? onClickGetHigherApr : onClickDeposit}
          css={{ w: { base: '100%', tablet: 296 }, alignSelf: 'center' }}
        />
      )}
    </div>
  );
};

export default InactiveAprCard;
